import React, { useEffect, useState } from "react";
import { camelCase } from "lodash";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import { Checkbox, ListItemText, MenuItem } from "@mui/material";
import "./transaction-filter.scss";
import {
  TRANSACTION_STATUS_OPTIONS,
  transactionFilterOptions,
} from "../../../order-management-helper";
import OnboardedDateFilter from "../../../../../tenant/tenant-list/tenant-filter-options/onboardeddate-filter";
import SubscriptionPlanFilter from "../../../../../tenant/tenant-list/tenant-filter-options/subscription-plan";
import LeadValueFilter from "../../../../../leads/lead-logs/leads-log-filters/leads-log-filter-options/lead-value-filter";

const TransactionFilters: React.FC<{
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: Record<string, any>, shouldClose?: boolean) => void;
  filters: Record<string, any>;
}> = ({ open, onClose, onApplyFilters, filters }) => {
  const [filterOptions, setFilterOptions] = useState(() =>
    transactionFilterOptions.map((opt) => ({ ...opt }))
  );

  const isAnyFilterSelected = filterOptions.some((opt) => opt.value);

  const handleCheckboxChange = (
    label: string,
    values: Record<string, any>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const updated = filterOptions.map((item) => {
      if (item.label === label) {
        const isChecked = !item.value;

        if (!isChecked) {
          if (label === "Date Range") {
            setFieldValue("transactionFromDate", null);
            setFieldValue("transactionToDate", null);
          } else if (label === "Amount") {
            setFieldValue("minAmount", "");
            setFieldValue("maxAmount", "");
          } else {
            const key = camelCase(label);
            setFieldValue(key, Array.isArray(values[key]) ? [] : "");
          }
        }

        return { ...item, value: isChecked };
      }
      return item;
    });

    setFilterOptions(updated);
  };

  const getInitialValues = () => {
    const values: Record<string, any> = {};
    filterOptions.forEach((option) => {
      if (option.label === "Date Range") {
        values["transactionFromDate"] = null;
        values["transactionToDate"] = null;
      } else if (option.label === "Amount") {
        values["minAmount"] = "";
        values["maxAmount"] = "";
      } else {
        const key = camelCase(option.label);
        if (option.label === "Status") {
          values[key] = [];
        } else {
          values[key] = "";
        }
      }
    });
    return values;
  };
  const [formValues, setFormValues] = useState(getInitialValues());

  const handleApply = (values: Record<string, any>) => {
    const selected = filterOptions
      .filter((opt) => opt.value)
      .reduce(
        (acc, curr) => {
          if (curr.label === "Date Range") {
            acc["transactionFromDate"] = values["transactionFromDate"];
            acc["transactionToDate"] = values["transactionToDate"];
          } else if (curr.label === "Amount") {
            acc["minAmount"] = values["minAmount"];
            acc["maxAmount"] = values["maxAmount"];
          } else {
            const key = camelCase(curr.label);
            acc[key] = values[key];
          }
          return acc;
        },
        {} as Record<string, any>
      );
    setFormValues(values);
    onApplyFilters(selected);
  };

  const validate = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    const minDate = dayjs("1900-01-01");

    filterOptions.forEach((option) => {
      if (option.value) {
        if (option.label === "Date Range") {
          const startDate = values["transactionFromDate"]
            ? dayjs(values["transactionFromDate"])
            : null;
          const endDate = values["transactionToDate"]
            ? dayjs(values["transactionToDate"])
            : null;

          if (!startDate) {
            errors["transactionFromDate"] = "Start date is required";
          } else if (startDate.isBefore(minDate)) {
            errors["transactionFromDate"] = "Invalid date";
          }

          if (!endDate) {
            errors["transactionToDate"] = "End date is required";
          } else if (endDate.isBefore(minDate)) {
            errors["transactionToDate"] = "Invalid date";
          }
        } else if (option.label === "Amount") {
          const minValue = Number(values["minAmount"]);
          const maxValue = Number(values["maxAmount"]);
          if (minValue && maxValue && minValue > maxValue) {
            errors["minAmount"] = "Min value should be less than max value";
          }
          if (!minValue) {
            errors["minAmount"] = "Min value is required";
          }
          if (minValue && maxValue && maxValue < minValue) {
            errors["maxAmount"] = "Max value should be greater than min value";
          }
          if (!maxValue) {
            errors["maxAmount"] = "Max value is required";
          }
          if (minValue < 0) {
            errors["minAmount"] = "Min value should be greater than 0";
          }
          if (maxValue < 0) {
            errors["maxAmount"] = "Max value should be greater than 0";
          }
          if (minValue && maxValue && minValue === maxValue) {
            errors["minAmount"] = "Min value should not be equal to max value";
          }
          if (minValue && !maxValue) {
            errors["maxAmount"] = "Max value is required";
          }
          if (maxValue && !minValue) {
            errors["minAmount"] = "Min value is required";
          }
        } else {
          const key = camelCase(option.label);
          if (
            !values[key] ||
            (Array.isArray(values[key]) && values[key].length === 0)
          ) {
            errors[key] = `${option.label} cannot be empty`;
          }
        }
      }
    });

    return errors;
  };

  const renderFilterField = (
    label: string,
    values: Record<string, any>,
    errors?: Record<string, any>,
    touched?: Record<string, any>,
    setFieldValue?: (field: string, value: any) => void,
    handleBlur?: (e: any) => void
  ) => {
    switch (label) {
      case "Date Range":
        return (
          <OnboardedDateFilter
            startDate={values.transactionFromDate}
            endDate={values.transactionToDate}
            onDateRangeChange={(start, end) => {
              if (start) setFieldValue?.("transactionFromDate", start);
              if (end) setFieldValue?.("transactionToDate", end);
            }}
            startDateError={
              touched?.transactionFromDate && errors?.transactionFromDate
                ? String(errors.transactionFromDate)
                : undefined
            }
            endDateError={
              touched?.transactionToDate && errors?.transactionToDate
                ? String(errors.transactionToDate)
                : undefined
            }
          />
        );
      case "Status":
        return (
          <SubscriptionPlanFilter
            value={Array.isArray(values["status"]) ? values["status"] : []}
            plans={TRANSACTION_STATUS_OPTIONS}
            label="Select Status"
            error={errors?.status}
            onChange={(val: string[]) => setFieldValue?.("status", val)}
            isMultiple={true}
          />
        );
      case "Amount":
        return (
          <LeadValueFilter
            minValue={values.minAmount}
            maxValue={values.maxAmount}
            onValueChange={(e: any, type: "min" | "max") => {
              if (type === "min") {
                setFieldValue?.("minAmount", e.target.value);
              } else {
                setFieldValue?.("maxAmount", e.target.value);
              }
            }}
            minError={touched?.minAmount && errors?.minAmount}
            maxError={touched?.maxAmount && errors?.maxAmount}
          />
        );
      default:
        return null;
    }
    return null;
  };

  useEffect(() => {
    const updatedOptions = transactionFilterOptions.map((opt) => {
      const key =
        opt.label === "Date Range"
          ? "transactionFromDate" in filters || "transactionToDate" in filters
          : opt.label === "Amount"
            ? "minAmount" in filters || "maxAmount" in filters
            : camelCase(opt.label) in filters;

      return { ...opt, value: !!key };
    });

    setFilterOptions(updatedOptions);

    setFormValues((prev) => ({
      ...getInitialValues(),
      ...filters,
    }));
  }, [filters]);

  return (
    <div className={`order-filter-drawer ${open ? "open" : ""}`}>
      <div className="filter-header">
        <div className="drawer-header">
          <h4>Filters</h4>
          <CloseIcon
            fontSize="small"
            onClick={onClose}
            style={{ cursor: "pointer" }}
          />
        </div>
        <h4>Filter By Fields</h4>
      </div>
      <Formik
        initialValues={formValues}
        enableReinitialize
        validate={validate}
        onSubmit={handleApply}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          handleSubmit,
          resetForm,
          handleBlur,
        }) => {
          return (
            <Form className="drawer-content" onSubmit={handleSubmit}>
              {filterOptions.map((option) => (
                <div key={option.label} className="filter-option-row">
                  <MenuItem
                    key={option.label}
                    value={option.label}
                    className="filter-menu-item"
                    onClick={() =>
                      handleCheckboxChange(option.label, values, setFieldValue)
                    }
                  >
                    <Checkbox checked={option.value} />
                    <ListItemText primary={option.label} />
                  </MenuItem>

                  {option.value &&
                    renderFilterField(
                      option.label,
                      values,
                      errors,
                      touched,
                      setFieldValue,
                      handleBlur
                    )}
                </div>
              ))}

              {isAnyFilterSelected && (
                <div className="lead-filter-buttons">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      resetForm();
                      setFormValues(getInitialValues());
                      setFilterOptions(
                        transactionFilterOptions.map((opt) => ({ ...opt }))
                      );
                      onApplyFilters({}, false);
                    }}
                  >
                    Reset
                  </button>
                  <button type="submit" className="next-button-wrapper">
                    Apply
                  </button>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
export default TransactionFilters;
